import type { TrainingPlan, Workout } from '@shared/types';
import { useEffect, useState } from 'react';
import { toISODateString } from '../utils/dateUtils';
import { getAllWorkouts } from '../utils/planUtils';

export interface WeekSummary {
  runningDistance: number;
  cyclingDistance: number;
  workoutCount: number;
  totalLoad: number;
  totalTSS: number;
  /** AI Coach week note/explanation */
  weekNote?: string;
  /** Week number (1-indexed) */
  weekNumber?: number;
}

/**
 * Hook for calculating week summary statistics
 */
export const useWeekSummary = (trainingPlan: TrainingPlan | null, currentWeekDates: Date[]) => {
  const [weekSummary, setWeekSummary] = useState<WeekSummary>({
    runningDistance: 0,
    cyclingDistance: 0,
    workoutCount: 0,
    totalLoad: 0,
    totalTSS: 0,
    weekNote: undefined,
    weekNumber: undefined,
  });

  const getWorkoutsForWeek = (dates: Date[]): Workout[] => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0) return [];

    const weekDateStrings = dates.map(toISODateString);
    return allWorkouts.filter(workout => weekDateStrings.includes(workout.training_date));
  };

  const calculateWeekSummary = (dates: Date[]) => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0) return;

    const weekWorkouts = getWorkoutsForWeek(dates);
    const activeWorkouts = weekWorkouts.filter(w => (w.name || '').toLowerCase() !== 'rest');

    // Find week info from the weeks array for this week's dates
    const weekDateStrings = dates.map(toISODateString);
    const week = trainingPlan?.weeks?.find(w => {
      // Check if the week's date range overlaps with current week dates
      return weekDateStrings.includes(w.weekStartDate) || weekDateStrings.includes(w.weekEndDate);
    });

    const summary = activeWorkouts.reduce(
      (acc, workout) => {
        const isCycling = workout.sportType === 'cycling';
        return {
          runningDistance: acc.runningDistance + (isCycling ? 0 : workout.total_distance || 0),
          cyclingDistance: acc.cyclingDistance + (isCycling ? workout.total_distance || 0 : 0),
          workoutCount: acc.workoutCount + 1,
          totalLoad: acc.totalLoad + (workout.TSS || 0),
          totalTSS: acc.totalTSS + (workout.TSS || 0),
        };
      },
      { runningDistance: 0, cyclingDistance: 0, workoutCount: 0, totalLoad: 0, totalTSS: 0 }
    );

    setWeekSummary({
      ...summary,
      weekNote: week?.note,
      weekNumber: week?.weekNumber,
    });
  };

  useEffect(() => {
    if (trainingPlan) {
      calculateWeekSummary(currentWeekDates);
    }
  }, [trainingPlan, currentWeekDates]);

  return {
    weekSummary,
    getWorkoutsForWeek,
  };
};
